import { useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  QuestionnaireStatus,
  useUpdateFormManagementQuestionnaireStatusMutation,
} from 'src/graphql';
import type { CategoryDetailsFormRow } from '../CategoryDetailsPage/types';

export interface UseFormDetailStatusActionsArgs {
  formRow: CategoryDetailsFormRow | null;
  onMenuClose: () => void;
  onArchiveSuccess: () => void;
}

export function useFormDetailStatusActions({
  formRow,
  onMenuClose,
  onArchiveSuccess,
}: UseFormDetailStatusActionsArgs) {
  const [formToArchive, setFormToArchive] =
    useState<CategoryDetailsFormRow | null>(null);
  const [formToUnpublish, setFormToUnpublish] =
    useState<CategoryDetailsFormRow | null>(null);

  const statusMutation = useUpdateFormManagementQuestionnaireStatusMutation();
  const queryClient = useQueryClient();

  const handleArchive = useCallback(() => {
    onMenuClose();
    if (formRow) setFormToArchive(formRow);
  }, [onMenuClose, formRow]);

  const handleUnpublish = useCallback(() => {
    onMenuClose();
    if (formRow) setFormToUnpublish(formRow);
  }, [onMenuClose, formRow]);

  const handleArchiveConfirm = useCallback(() => {
    if (!formToArchive) return;
    const archivedTitle = formToArchive.title;
    statusMutation.mutate(
      {
        id: formToArchive.id,
        input: { status: QuestionnaireStatus.Archived },
      },
      {
        onSuccess: () => {
          setFormToArchive(null);
          handleSuccessToast({
            toastMessage: 'Form archived',
            toastDescription: `"${archivedTitle}" has been archived.`,
          });
          setTimeout(onArchiveSuccess, 0);
        },
        onError: () => {
          setFormToArchive(null);
          handleErrorToast({
            toastMessage: 'Failed to archive form',
            toastDescription: 'Something went wrong. Please try again.',
          });
        },
      },
    );
  }, [formToArchive, statusMutation, onArchiveSuccess]);

  const handleUnpublishConfirm = useCallback(() => {
    if (!formToUnpublish) return;
    const unpublishedTitle = formToUnpublish.title;
    statusMutation.mutate(
      {
        id: formToUnpublish.id,
        input: { status: QuestionnaireStatus.Inactive },
      },
      {
        onSuccess: () => {
          setFormToUnpublish(null);
          handleSuccessToast({
            toastMessage: 'Form unpublished',
            toastDescription: `"${unpublishedTitle}" has been unpublished.`,
          });
          void queryClient.invalidateQueries([
            'FormManagementQuestionnaireById',
          ]);
          // Unpublishing the last published form flips its category back to
          // "Form required", so the list and category data go stale too.
          void queryClient.invalidateQueries(['FormManagementQuestionnaires']);
          void queryClient.invalidateQueries(['GetOrganisationCategories']);
        },
        onError: () => {
          setFormToUnpublish(null);
          handleErrorToast({
            toastMessage: 'Failed to unpublish form',
            toastDescription: 'Something went wrong. Please try again.',
          });
        },
      },
    );
  }, [formToUnpublish, statusMutation, queryClient]);

  return {
    formToArchive,
    setFormToArchive,
    handleArchive,
    handleArchiveConfirm,
    formToUnpublish,
    setFormToUnpublish,
    handleUnpublish,
    handleUnpublishConfirm,
    isStatusMutationLoading: statusMutation.isLoading,
  };
}
